import { ThemeProvider } from "@mui/material";
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router';
import AHDate from "_shared/dates/date";
import { sessionRecordingChoice, useSessionRecordingChoice } from "_shared/session-recording/session-recording-choice";
import { SESSION_RECORDING_UNMASK_CLASS } from "_shared/session-recording/session-recording-privacy";

const Copyright = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  ...theme.applyStyles('light', {
    color: 'rgba(0, 0, 0, 0.54)'
  })
}));

const FooterLinks = styled('section')(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  '& ul': {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    rowGap: theme.spacing(0.5),
    '& li': {
      '&:last-child': {
        border: 'none'
      },
      borderRight: '1px solid',
      borderColor: theme.palette.mode === 'light' ? '#3f51b5' : '#fff',
      flexBasis: 'auto',
      padding: '0 1em',
      textAlign: 'center'
    },
    // Once the row wraps, the divider on the last item of each visual line points at nothing —
    // there is no selector for "last on this line" — so the narrow layout spaces the links instead
    // of ruling between them.
    [theme.breakpoints.down('md')]: {
      '& li': {
        border: 'none',
        padding: theme.spacing(0, 1)
      }
    }
  }
}));

const FooterContainer = styled('footer')(({ theme }) => ({
  display: 'block',
  flexShrink: 0,
  fontSize: '0.8em',
  margin: 'auto',
  padding: theme.spacing(2, 0),
  textAlign: "center",
  width: '100%',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2.75, 0, 2.75, 0)
  }
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.mode === 'light' ? '#3f51b5' : 'white'
}));

/**
 * Reopens the cookie question. A button, not a link: it goes nowhere, it asks again.
 * Styled as a link so it reads as one of its neighbours.
 */
const FooterButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  color: theme.palette.mode === 'light' ? '#3f51b5' : 'white',
  cursor: 'pointer',
  font: 'inherit',
  padding: 0,
  textDecoration: 'underline'
}));

type Props = {
  mode?: string;
};

/**
 * The links are rendered at every width. They used to be dropped below `md`, and nothing else in
 * the app links to `/privacy-policy` or `/terms-of-use`, so on a phone they were unreachable
 * altogether rather than merely inconvenient. The list wraps instead.
 *
 * "Cookie Preferences" sits beside the Privacy Policy so that withdrawing consent is as easy to find
 * as giving it was. It lives here rather than in a layout because three layouts mount this footer
 * separately, and each of them has to offer it. It shows only while someone is signed in: nothing
 * is recorded before sign-in, so on the login page there is no answer to change.
 */
export default function Footer(props: Readonly<Props>) {
  const { isSignedIn } = useSessionRecordingChoice();

  return (
    <ThemeProvider theme={theme => ({
      ...theme,
      palette: {
        mode: props.mode ?? 'light'
      }
    })}>
      {/* Static links and a copyright line: nothing of anyone's, so session replays may read it. */}
      <FooterContainer id='siteFooter' className={SESSION_RECORDING_UNMASK_CLASS}>
        <Copyright variant="body2" color="textSecondary">
          {`Copyright \u00A9 Attorney Hub, LLC ${new AHDate().year}`}
        </Copyright>
        <FooterLinks>
          <ul>
            <li><FooterLink to="/privacy-policy">Privacy Policy</FooterLink></li>
            {isSignedIn && (
              <li>
                <FooterButton type="button" onClick={sessionRecordingChoice.reopen}>Cookie Preferences</FooterButton>
              </li>
            )}
            <li><FooterLink to="/terms-of-use">Terms of Use</FooterLink></li>
            <li><FooterLink to="/about-us">About Us</FooterLink></li>
            <li><FooterLink to="mailto:contactus@attorneyhub.com">Contact Us</FooterLink></li>
          </ul>
        </FooterLinks>
      </FooterContainer>
    </ThemeProvider>
  );
}
